import IconCheck from "@/src/assets/icons/icon-check-shape.svg";
import CompletedIcon from "@/src/assets/icons/icon-completed.svg";
import { Button } from "@/src/components/atom/Button";
import Icon from "@/src/components/atom/Icon";
import Typo from "@/src/components/atom/Typo";
import CallOut from "@/src/components/molecule/CallOut";
import SectionCardWithoutHeader from "@/src/components/molecule/SectionCardWithoutHeader";
import DATE_FORMAT_STRINGS from "@/src/constant/dateFormat";
import { getFormattedTimezoneDateWithOffset } from "@/src/hooks/useCompanyLocalDate";
import EXPORTER_PRIVATE_PATH from "@/src/routes/exporter/path";
import { useGetCurrentSubscriptionQuery } from "@/src/store/apis/subscription";
import { authApi, useEditExporterUserAdditionalInfosMutation } from "@/src/store/apis/auth";
import colorSet from "@/src/styles/color";
import { isUndefined } from "@/src/utils/is";
import dayjs from "dayjs";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

function FreeSignupCompleted() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: subscription } = useGetCurrentSubscriptionQuery();
  const [editExporterUserAdditionalInfos] = useEditExporterUserAdditionalInfosMutation();

  // Get gmtOffset from session
  const { gmtOffset } = authApi.endpoints.getSession.useQueryState(undefined, {
    selectFromResult: ({ currentData, isError, isFetching }) => {
      const isUnstable = isError || isFetching || isUndefined(currentData);
      const isStable = !isUnstable;

      return {
        gmtOffset: isStable ? currentData.row.exporter.gmtOffset || 0 : 0,
      };
    },
  });

  const goToHome = async () => {
    try {
      // 🎯 서버 기반 첫 로그인 완료 처리
      await editExporterUserAdditionalInfos({
        isInitialGuideNeeded: false,
      });
      
      navigate(EXPORTER_PRIVATE_PATH.HOME, { replace: true });
    } catch (error) {
      console.error('첫 로그인 완료 처리 실패:', error);
      // 에러가 나도 홈으로는 이동시킴
      navigate(EXPORTER_PRIVATE_PATH.HOME, { replace: true });
    }
  };

  // 구독 정보가 없으면 컴포넌트를 렌더링하지 않음
  if (!subscription?.subscriptionStartAtFrom || !subscription?.subscriptionEndAtTo) {
    return null;
  }

  const getFreeTrialInfo = () => {
    // 백엔드에서 계산된 현지시간 날짜 사용 (localStartDate, localEndDate가 있으면 사용)
    const startDate = subscription.localStartDate ||
      getFormattedTimezoneDateWithOffset({
        date: subscription.subscriptionStartAtFrom,
        format: DATE_FORMAT_STRINGS.YYYY_MM_DD,
        gmtOffset,
      });

    const endDate = subscription.localEndDate ||
      getFormattedTimezoneDateWithOffset({
        date: subscription.subscriptionEndAtTo,
        format: DATE_FORMAT_STRINGS.YYYY_MM_DD,
        gmtOffset,
      });

    // 무료체험 전체 기간 계산 (시작일과 종료일의 차이)
    let trialDays = 0;
    if (subscription.localStartDate && subscription.localEndDate) {
      // localDate를 사용한 계산
      const start = dayjs(subscription.localStartDate);
      const end = dayjs(subscription.localEndDate);
      trialDays = end.diff(start, 'days') + 1; // 당일 포함
    } else {
      // UTC 날짜를 사용한 계산
      const start = dayjs(subscription.subscriptionStartAtFrom);
      const end = dayjs(subscription.subscriptionEndAtTo);
      trialDays = Math.ceil(end.diff(start, 'hours') / 24);
    }

    return {
      startDate,
      endDate,
      daysLeft: trialDays, // 전체 무료체험 기간
    };
  };

  return (
    <StyledSectionCard>
      <FlexColumn gap={40}>
        {/* Information */}
        <PlanInfo>
          <FlexColumn alignItems="center">
            <Icon iconSrc={CompletedIcon} iconSize={60} />
          </FlexColumn>

          <FlexColumn gap={12} alignItems="center">
            <Typo typoType="d6">
              {t("enterpriseSignup:planSubscriptionCompleted")}
            </Typo>
            <Typo>
              {t("enterpriseSignup:planSubscriptionCompletedDescription")}
            </Typo>
          </FlexColumn>

          <StyledCallout backgroundColor="white">
            <FlexColumn gap={16}>
              <FlexColumn gap={4}>
                <Typo typoType="h4">{t("enterpriseSignup:freeTrial")}</Typo>

                <FlexRow gap={4}>
                  <Typo typoType="b7m">
                    {getFreeTrialInfo().startDate}–{getFreeTrialInfo().endDate}
                  </Typo>
                  <FreeTrialBadge>
                    <BadgeText>
                      {getFreeTrialInfo().daysLeft}{t("enterpriseSignup:freeTrialPeriod")}
                    </BadgeText>
                  </FreeTrialBadge>
                </FlexRow>
              </FlexColumn>

              <Divider />

              <FlexRow gap={4}>
                <StyledCheckIcon iconSrc={IconCheck} iconSize={16} />
                <NoticeText>
                  {t("enterpriseSignup:freeTrialNotice")}
                </NoticeText>
              </FlexRow>
            </FlexColumn>
          </StyledCallout>
        </PlanInfo>

        <StyledButton
          buttonColor="blue"
          buttonGrade="secondary"
          onClick={goToHome}
        >
          {t("enterpriseSignup:button.home")}
        </StyledButton>
      </FlexColumn>
    </StyledSectionCard>
  );
}

export default FreeSignupCompleted;

const StyledSectionCard = styled(SectionCardWithoutHeader)`
  width: 640px;
  padding: 40px 48px;
`;

const FlexColumn = styled.div<{
  gap?: number;
  alignItems?: CSSProperties["alignItems"];
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap}px;
  align-items: ${({ alignItems }) => alignItems};
  width: 100%;
`;

const FlexRow = styled(FlexColumn)`
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
`;

const PlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px 32px 0;
  gap: 40px;
`;

const StyledButton = styled(Button)`
  text-align: center;
`;

const StyledCallout = styled(CallOut)``;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${colorSet.gray9};
`;

const FreeTrialBadge = styled.div`
  padding: 2px 10px;
  background: #E5F2FF;
  border-radius: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const FONT_FAMILY = '"Pretendard", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const BadgeText = styled.span`
  font-family: ${FONT_FAMILY};
  font-style: normal;
  font-weight: 500;
  font-size: 11px;
  line-height: 16px;
  display: flex;
  align-items: center;
  color: #007AFF;
`;

const NoticeText = styled.span`
  flex: 1;
  font-family: ${FONT_FAMILY};
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #222729;
`;

const StyledCheckIcon = styled(Icon)`
  margin-top: 4px;
`;